import type { ProductDto } from '@pos/api-client';
import { ApiError } from '@pos/api-client';
import { UNITS, formatAmount, formatQuantity, parseMoney } from '@pos/business-logic';
import type { ProductUnit } from '@pos/types';
import type { ProductInput } from '@pos/validation';
import { BottomSheet, Button, EmptyState, ErrorState, Field, IconButton, Input, ListGroup, ListRow, Money, SearchField, Segmented, Select, Skeleton, Switch, cn, confirmAction, toast } from '@pos/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Pencil, Plus, ScanLine, Wand2 } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api } from '../../app/auth';
import { HeaderButton, Screen } from '../../app/Screen';
import { lookupCode, useCategories, useProduct, useProductsPaged } from '../../app/data';
import { ScannerSheet } from '../../lib/Scanner';

/** Full input for PATCH from the current card, with the fields the phone edits replaced. */
function toInput(p: ProductDto, patch: Partial<ProductInput>): ProductInput {
  const extra = p.barcodes.filter((b) => b.code !== p.barcode && !p.variants.some((v) => v.barcode === b.code));
  return {
    name: p.name,
    categoryId: p.categoryId,
    brandId: p.brandId,
    supplierId: p.supplierId,
    sku: p.sku,
    article: p.article,
    barcode: p.barcode,
    unit: p.unit,
    isWeighted: p.isWeighted,
    purchasePrice: p.purchasePrice,
    sellingPrice: p.sellingPrice,
    discountPrice: p.discountPrice,
    taxRate: p.taxRate,
    minStock: p.minStock,
    description: p.description,
    serialTracking: p.serialTracking,
    warrantyMonths: p.warrantyMonths,
    trackExpiration: p.trackExpiration,
    attributes: p.attributes,
    customFields: p.customFields,
    imageUrl: p.imageUrl,
    isActive: p.isActive,
    variants: p.variants.map((v) => ({ id: v.id, name: v.name, sku: v.sku, barcode: v.barcode, attributes: v.attributes, sellingPrice: v.sellingPrice, purchasePrice: v.purchasePrice, isActive: v.isActive })),
    barcodes: extra.map((b) => ({ code: b.code, quantity: b.quantity, variantId: null })),
    version: p.version,
    ...patch,
  };
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'VERSION_CONFLICT') return 'Карточку только что изменили. Откройте её заново.';
    if (e.fields) return Object.values(e.fields)[0] ?? e.message;
    return e.message;
  }
  return 'Проверьте связь и повторите';
}

export function ProductsPage() {
  const [q, setQ] = useState('');
  const query = useDeferredValue(q.trim());
  const products = useProductsPaged(query);
  const navigate = useNavigate();
  const [scan, setScan] = useState(false);
  const pages = products.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);
  const total = pages[0]?.total ?? 0;

  const onCode = async (code: string) => {
    setScan(false);
    const hit = await lookupCode(code);
    if (hit) navigate(`/products/${hit.product.id}`);
    else navigate(`/products/new?barcode=${encodeURIComponent(code)}`);
  };

  return (
    <Screen
      title="Товары"
      actions={
        <HeaderButton label="Новый товар" onClick={() => navigate('/products/new')}>
          <Plus />
        </HeaderButton>
      }
    >
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <SearchField size="md" value={q} onChange={(e) => setQ(e.target.value)} onClear={() => setQ('')} placeholder="Название, артикул, штрихкод" aria-label="Поиск товара" className="flex-1" />
          <IconButton label="Сканировать штрихкод" variant="secondary" onClick={() => setScan(true)}>
            <ScanLine className="size-5" />
          </IconButton>
        </div>
        {products.data && (
          <p className="px-1 text-sm text-ink-3 tnum">
            {query ? `Найдено ${total}` : `${total} в каталоге`}
            {items.length < total ? ` · показано ${items.length}` : ''}
          </p>
        )}
        {products.isError ? (
          <ErrorState description="Не удалось загрузить товары." onRetry={() => void products.refetch()} />
        ) : products.isPending ? (
          <Skeleton className="h-80 rounded-lg" />
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-line bg-surface">
            <EmptyState
              icon={<Package />}
              title={query ? 'Ничего не найдено' : 'Товаров пока нет'}
              description={query ? undefined : 'Каталог общий для всех касс.'}
              action={<Button onClick={() => navigate('/products/new')}>Добавить товар</Button>}
            />
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-line bg-surface">
            {items.map((p) => (
              <ListRow
                key={p.id}
                title={<span className={cn(!p.isActive && 'text-ink-4')}>{p.name}</span>}
                subtitle={[p.categoryName, p.stock <= 0 ? 'нет в наличии' : `в наличии ${formatQuantity(p.stock, p.unit)}`].filter(Boolean).join(' · ')}
                trailing={<Money value={p.discountPrice ?? p.sellingPrice} className="font-semibold" />}
                onClick={() => navigate(`/products/${p.id}`)}
              />
            ))}
            {products.hasNextPage && (
              <div className="border-t border-line p-3">
                <Button size="lg" className="w-full" loading={products.isFetchingNextPage} onClick={() => void products.fetchNextPage()}>
                  Показать ещё · {total - items.length} осталось
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <ScannerSheet open={scan} onOpenChange={setScan} onCode={(c) => void onCode(c)} />
    </Screen>
  );
}

function EditSheet({ product, open, onOpenChange }: { product: ProductDto; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [selling, setSelling] = useState(String(product.sellingPrice));
  const [purchase, setPurchase] = useState(String(product.purchasePrice));
  const [minStock, setMinStock] = useState(String(product.minStock / 1000));
  const [barcode, setBarcode] = useState(product.barcode ?? '');
  const [active, setActive] = useState(product.isActive);
  const [scan, setScan] = useState(false);
  const sellingV = parseMoney(selling);
  const purchaseV = purchase.trim() ? parseMoney(purchase) : 0;
  const minV = Math.round(Number(minStock.replace(',', '.') || 0) * 1000);
  const valid = sellingV !== null && sellingV > 0 && purchaseV !== null && Number.isFinite(minV) && minV >= 0;
  const save = useMutation({
    mutationFn: () =>
      api.patch<ProductDto>(
        `/products/${product.id}`,
        toInput(product, { sellingPrice: sellingV ?? 0, purchasePrice: purchaseV ?? 0, minStock: minV, barcode: barcode.trim() || null, isActive: active }),
      ),
    onSuccess: async (p) => {
      qc.setQueryData(['product', p.id], p);
      await qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('Сохранено', { description: 'Кассы получат изменения при следующей синхронизации' });
      onOpenChange(false);
    },
    onError: (e) => toast.error('Не сохранено', { description: errorText(e) }),
  });
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Изменить товар"
      description={product.name}
      footer={
        <Button size="xl" variant="primary" className="w-full" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Цена продажи" error={sellingV === null || sellingV <= 0 ? 'Укажите цену' : undefined}>
            {(a) => <Input {...a} size="lg" inputMode="numeric" className="tnum" value={selling} onChange={(e) => setSelling(e.target.value)} />}
          </Field>
          <Field label="Закупка">
            {(a) => <Input {...a} size="lg" inputMode="numeric" className="tnum" value={purchase} onChange={(e) => setPurchase(e.target.value)} />}
          </Field>
        </div>
        <Field label={`Минимальный остаток, ${UNITS[product.unit].label}`} hint="Ниже — товар попадёт в «Заканчиваются»">
          {(a) => <Input {...a} size="lg" inputMode="decimal" className="tnum" value={minStock} onChange={(e) => setMinStock(e.target.value)} />}
        </Field>
        <Field label="Штрихкод">
          {(a) => (
            <div className="flex gap-2">
              <Input {...a} size="lg" inputMode="numeric" className="flex-1 tnum" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
              <IconButton label="Сканировать штрихкод" variant="secondary" size="lg" onClick={() => setScan(true)}>
                <ScanLine className="size-5" />
              </IconButton>
            </div>
          )}
        </Field>
        <label className="flex min-h-12 items-center justify-between gap-3 text-md">
          В продаже
          <Switch checked={active} onCheckedChange={setActive} label="В продаже" />
        </label>
      </div>
      <ScannerSheet
        open={scan}
        onOpenChange={setScan}
        onCode={(c) => {
          setBarcode(c);
          setScan(false);
        }}
      />
    </BottomSheet>
  );
}

export function ProductPage() {
  const { id } = useParams();
  const { data: p, isPending, isError, refetch } = useProduct(id);
  const [edit, setEdit] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (productId: string) => api.delete(`/products/${productId}`),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: ['products'] }), qc.invalidateQueries({ queryKey: ['products-paged'] }), qc.invalidateQueries({ queryKey: ['stock'] })]);
      toast.success('Товар удалён');
      navigate('/products');
    },
    onError: (e) => toast.error('Не удалено', { description: e instanceof ApiError ? e.message : undefined }),
  });
  return (
    <Screen
      title={p?.name ?? 'Товар'}
      back="/products"
      actions={
        p ? (
          <HeaderButton label="Изменить" onClick={() => setEdit(true)}>
            <Pencil />
          </HeaderButton>
        ) : undefined
      }
    >
      {isError ? (
        <ErrorState description="Товар не найден или нет связи." onRetry={() => void refetch()} />
      ) : isPending || !p ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : (
        <>
          <div className="mb-6 rounded-2xl border border-line bg-surface p-4">
            <h2 className="text-xl font-semibold">{p.name}</h2>
            <p className="mt-0.5 text-sm text-ink-3">{[p.categoryName, p.brandName, !p.isActive && 'снят с продажи'].filter(Boolean).join(' · ') || 'Без категории'}</p>
            <div className="mt-4 flex items-end justify-between gap-3">
              <div>
                <p className="text-xs text-ink-3">Цена</p>
                <Money value={p.discountPrice ?? p.sellingPrice} className="text-3xl font-bold" />
                {p.discountPrice !== null && <p className="text-sm text-ink-4 line-through tnum">{formatAmount(p.sellingPrice)}</p>}
              </div>
              <div className="text-right">
                <p className="text-xs text-ink-3">В наличии</p>
                <p className={cn('text-2xl font-semibold tnum', p.stock <= 0 ? 'text-danger' : p.minStock > 0 && p.stock <= p.minStock ? 'text-warning' : 'text-ink')}>
                  {formatQuantity(p.stock, p.unit)}
                </p>
              </div>
            </div>
          </div>
          <ListGroup title="Данные">
            <ListRow title="Закупочная цена" trailing={<span className="tnum">{formatAmount(p.purchasePrice)}</span>} />
            <ListRow
              title="Наценка"
              trailing={<span className="tnum">{p.purchasePrice > 0 ? `${Math.round(((p.sellingPrice - p.purchasePrice) / p.purchasePrice) * 100)}%` : '—'}</span>}
            />
            <ListRow title="Штрихкод" trailing={<span className="tnum">{p.barcode ?? '—'}</span>} />
            {p.sku && <ListRow title="Артикул" trailing={p.sku} />}
            <ListRow title="Минимальный остаток" trailing={<span className="tnum">{p.minStock ? formatQuantity(p.minStock, p.unit) : '—'}</span>} />
          </ListGroup>
          {p.variants.length > 0 && (
            <ListGroup title="Варианты">
              {p.variants.map((v) => (
                <ListRow key={v.id} title={v.name} subtitle={v.barcode ?? undefined} trailing={<span className="tnum">{formatQuantity(v.stock, p.unit)}</span>} />
              ))}
            </ListGroup>
          )}
          <Button
            size="lg"
            variant="ghost"
            className="mt-6 w-full text-danger"
            loading={remove.isPending}
            onClick={() =>
              void confirmAction({
                title: `Удалить «${p.name}»?`,
                description: 'Товар пропадёт из каталога и с касс. Проданные чеки и отчёты сохранятся; штрихкод освободится.',
                confirmLabel: 'Удалить',
              }).then((ok) => ok && remove.mutate(p.id))
            }
          >
            Удалить товар
          </Button>
          <EditSheet key={`${p.id}:${p.version}`} product={p} open={edit} onOpenChange={setEdit} />
        </>
      )}
    </Screen>
  );
}

const UNIT_CHOICES: { value: ProductUnit; label: string }[] = [
  { value: 'PIECE', label: 'Штуки' },
  { value: 'KG', label: 'Кг' },
  { value: 'LITER', label: 'Литры' },
  { value: 'METER', label: 'Метры' },
];

export function NewProductPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const categories = useCategories();
  const initialBarcode = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('barcode') ?? '';
  const [name, setName] = useState('');
  const [unit, setUnit] = useState<ProductUnit>('PIECE');
  const [selling, setSelling] = useState('');
  const [purchase, setPurchase] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [barcode, setBarcode] = useState(initialBarcode);
  const [scan, setScan] = useState(false);
  const sellingV = parseMoney(selling);
  const purchaseV = purchase.trim() ? parseMoney(purchase) : 0;
  const valid = name.trim().length > 0 && sellingV !== null && sellingV > 0 && purchaseV !== null;

  const generate = async () => {
    try {
      setBarcode((await api.get<{ barcode: string }>('/products/barcode/next')).barcode);
    } catch {
      toast.error('Не удалось получить штрихкод');
    }
  };
  const create = useMutation({
    mutationFn: () =>
      api.post<ProductDto>('/products', {
        name: name.trim(),
        unit,
        isWeighted: unit === 'KG',
        sellingPrice: sellingV,
        purchasePrice: purchaseV,
        categoryId,
        barcode: barcode.trim() || null,
      }),
    onSuccess: async (p) => {
      await qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('Товар добавлен', { description: 'Появится на кассах после синхронизации' });
      navigate(`/products/${p.id}`, { replace: true });
    },
    onError: (e) => toast.error('Товар не создан', { description: errorText(e) }),
  });

  return (
    <Screen title="Новый товар" back="/products">
      <form
        className="space-y-4 pb-8"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) create.mutate();
        }}
      >
        <Field label="Название" required>
          {(a) => <Input {...a} size="lg" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Например: Вода 1,5 л" />}
        </Field>
        <Field label="Как продаётся">
          {() => <Segmented label="Единица" value={unit} onChange={setUnit} options={UNIT_CHOICES} className="w-full [&>button]:flex-1" />}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Цена за ${UNITS[unit].label}`} required>
            {(a) => <Input {...a} size="lg" inputMode="numeric" className="tnum" value={selling} onChange={(e) => setSelling(e.target.value)} />}
          </Field>
          <Field label="Закупка">
            {(a) => <Input {...a} size="lg" inputMode="numeric" className="tnum" value={purchase} onChange={(e) => setPurchase(e.target.value)} />}
          </Field>
        </div>
        <Field label="Категория">
          {(a) => (
            <Select
              {...a}
              value={categoryId ?? '__none'}
              onValueChange={(v) => setCategoryId(v === '__none' ? null : v)}
              options={[{ value: '__none', label: 'Без категории' }, ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))]}
            />
          )}
        </Field>
        <Field label="Штрихкод" hint="Сканируйте с упаковки или создайте свой для весового и штучного товара">
          {(a) => (
            <div className="flex gap-2">
              <Input {...a} size="lg" inputMode="numeric" className="flex-1 tnum" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
              <IconButton label="Сканировать штрихкод" variant="secondary" size="lg" onClick={() => setScan(true)}>
                <ScanLine className="size-5" />
              </IconButton>
              <IconButton label="Создать штрихкод" variant="secondary" size="lg" onClick={() => void generate()}>
                <Wand2 className="size-5" />
              </IconButton>
            </div>
          )}
        </Field>
        <Button type="submit" size="xl" variant="primary" className="w-full" disabled={!valid} loading={create.isPending}>
          Добавить товар
        </Button>
      </form>
      <ScannerSheet
        open={scan}
        onOpenChange={setScan}
        onCode={(c) => {
          setBarcode(c);
          setScan(false);
        }}
      />
    </Screen>
  );
}
